import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { createPortal } from 'react-dom';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.cssText = `
        position: fixed !important;
        top: -${scrollY}px !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        overflow: hidden !important;
        width: 100% !important;
        height: 100% !important;
      `;
      document.documentElement.style.cssText = 'overflow: hidden !important;';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.cssText = '';
      document.documentElement.style.cssText = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      document.body.style.cssText = '';
      document.documentElement.style.cssText = '';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Basements', path: '/basements' },
    { name: 'Bathrooms', path: '/bathrooms' },
    { name: 'Custom Features', path: '/custom-features' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  // Mobile Menu Component - rendered via Portal to document.body
  const MobileMenu = () => {
    if (!isMobileMenuOpen) return null;
    
    return createPortal(
      <>
        {/* Inject styles */}
        <style>{`
          .mobile-overlay {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            height: 100dvh !important;
            background: #000000 !important;
            opacity: 1 !important;
            z-index: 99998 !important;
            cursor: pointer;
            margin: 0 !important;
            padding: 0 !important;
          }
          .mobile-drawer {
            position: fixed !important;
            top: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 85vw !important;
            max-width: 320px !important;
            height: 100vh !important;
            height: 100dvh !important;
            background-color: #ffffff !important;
            z-index: 99999 !important;
            display: flex !important;
            flex-direction: column !important;
            box-shadow: -10px 0 40px rgba(0, 0, 0, 0.4) !important;
            overflow: hidden !important;
          }
          .mobile-drawer-header {
            height: 72px;
            min-height: 72px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 20px;
            border-bottom: 1px solid #e5e7eb;
            background: #ffffff;
          }
          .mobile-drawer-content {
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
            background: #ffffff;
          }
          .mobile-nav-link {
            display: block;
            padding: 18px 24px;
            font-size: 17px;
            font-weight: 500;
            color: #1e293b;
            text-decoration: none;
            border-left: 4px solid transparent;
            transition: all 0.15s ease;
          }
          .mobile-nav-link:hover {
            background: #f8fafc;
            color: #c5a059;
          }
          .mobile-nav-link.active {
            color: #c5a059;
            background: #fefbf6;
            border-left-color: #c5a059;
            font-weight: 600;
          }
          .mobile-cta-section {
            padding: 20px 24px;
            background: #ffffff;
            border-top: 1px solid #e5e7eb;
          }
          .mobile-cta-primary {
            display: block;
            width: 100%;
            padding: 16px;
            background: #0f172a;
            color: #ffffff;
            text-align: center;
            text-decoration: none;
            font-size: 14px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            border-radius: 4px;
            margin-bottom: 12px;
            border: none;
          }
          .mobile-cta-secondary {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            width: 100%;
            padding: 16px;
            background: #ffffff;
            color: #0f172a;
            text-align: center;
            text-decoration: none;
            font-size: 14px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            border: 2px solid #0f172a;
            border-radius: 4px;
          }
          .mobile-footer {
            padding: 20px 24px;
            background: #0f172a;
            flex-shrink: 0;
          }
          .mobile-footer-label {
            font-size: 11px;
            color: #64748b;
            margin-bottom: 12px;
            text-transform: uppercase;
            letter-spacing: 0.15em;
            font-weight: 500;
          }
          .mobile-footer-link {
            display: flex;
            align-items: center;
            gap: 12px;
            color: #ffffff;
            text-decoration: none;
            margin-bottom: 10px;
            font-size: 15px;
          }
          .mobile-footer-link:last-child {
            margin-bottom: 0;
            font-size: 13px;
            color: #e2e8f0;
          }
        `}</style>

        {/* Full Screen Backdrop Overlay */}
        <div
          className="mobile-overlay"
          onClick={closeMobileMenu}
          data-testid="mobile-menu-overlay"
          aria-hidden="true"
        />

        {/* Menu Drawer */}
        <div className="mobile-drawer" data-testid="mobile-menu">
          {/* Header */}
          <div className="mobile-drawer-header">
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', fontWeight: 700, color: '#0f172a' }}>
              Menu
            </span>
            <button
              onClick={closeMobileMenu}
              data-testid="mobile-menu-close"
              style={{
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f1f5f9',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
              aria-label="Close menu"
            >
              <X size={24} color="#0f172a" strokeWidth={2.5} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="mobile-drawer-content">
            {/* Navigation Links */}
            <nav style={{ padding: '12px 0' }}>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={closeMobileMenu}
                  className={`mobile-nav-link ${location.pathname === link.path ? 'active' : ''}`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* CTA Section */}
            <div className="mobile-cta-section">
              <Link
                to="/contact"
                onClick={closeMobileMenu}
                className="mobile-cta-primary"
                data-testid="mobile-cta-button"
              >
                Request Free Estimate
              </Link>
              <a
                href="tel:403-402-6596"
                className="mobile-cta-secondary"
                data-testid="mobile-phone-button"
              >
                <Phone size={18} />
                Call 403-402-6596
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="mobile-footer">
            <p className="mobile-footer-label">Contact Us</p>
            <a href="tel:403-402-6596" className="mobile-footer-link">
              <Phone size={16} color="#c5a059" />
              403-402-6596
            </a>
            <a href="mailto:info@yyccustombasement.ca" className="mobile-footer-link">
              <Mail size={16} color="#c5a059" />
              info@yyccustombasement.ca
            </a>
          </div>
        </div>
      </>,
      document.body
    );
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#0f172a] text-white py-2 px-6 hidden md:block">
        <div className="max-w-[1400px] mx-auto flex justify-between items-center text-xs tracking-wider">
          <div className="flex items-center gap-6">
            <span className="text-slate-400">Calgary's Trusted Renovation Experts</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="tel:403-402-6596" className="flex items-center gap-2 hover:text-[#c5a059] transition-colors" data-testid="top-phone-link">
              <Phone size={12} />
              <span>403-402-6596</span>
            </a>
            <a href="mailto:info@yyccustombasement.ca" className="flex items-center gap-2 hover:text-[#c5a059] transition-colors" data-testid="top-email-link">
              <Mail size={12} />
              <span>info@yyccustombasement.ca</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm'
            : 'bg-white'
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex flex-col" data-testid="logo-link">
              <span className="font-heading text-xl md:text-2xl font-bold text-[#0f172a] tracking-tight">
                YYC Custom
              </span>
              <span className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-slate-500 -mt-1">
                Basement & Bath
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${
                    location.pathname === link.path ? 'text-[#c5a059]' : ''
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link
                to="/contact"
                className="btn-primary inline-block"
                data-testid="nav-cta-button"
              >
                Free Estimate
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={toggleMobileMenu}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X size={28} className="text-[#0f172a]" />
              ) : (
                <Menu size={28} className="text-[#0f172a]" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu />
    </>
  );
};

export default Navigation;
